import React, { useEffect, useState } from 'react';
import { reportsAPI, adminAPI } from '@/services/api';
import ReportDialog from '@/components/shared/ReportDialog';
import { FileSpreadsheet, Download, AlertCircle, Building2, User, Crown, Globe2, List } from 'lucide-react';

const GROUP_OPTIONS = [
  { key: 'none', label: 'Job rows (every posting)', icon: List, desc: 'One row per job with optional columns including org, recruiter, matches.' },
  { key: 'org', label: 'Group by organisation', icon: Building2, desc: 'One row per organisation with total/active/archived jobs and recruiter count.' },
  { key: 'recruiter', label: 'Group by recruiter', icon: User, desc: 'One row per recruiter with their job counts, role, and first/last post.' },
  { key: 'head', label: 'Group by recruiter head', icon: Crown, desc: "One row per head with their team's total jobs and team size." },
  { key: 'all', label: 'Platform-wide totals', icon: Globe2, desc: 'A single row with overall jobs, orgs, recruiters, and matches.' },
];

export default function AdminReports() {
  const [meta, setMeta] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [openGroup, setOpenGroup] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [m, h] = await Promise.all([
          reportsAPI.adminColumns(),
          adminAPI.getOrgHierarchy().catch(() => ({ organizations: [] })),
        ]);
        setMeta(m);
        setOrgs(h.organizations || []);
      } catch (e) {
        setError(e.response?.data?.detail || e.message);
      }
    })();
  }, []);

  const today = new Date().toISOString().slice(0, 10);
  const monthAgo = new Date(Date.now() - 30 * 86400_000).toISOString().slice(0, 10);

  const columnsFor = (group) => {
    if (!meta) return [];
    if (group === 'none') return meta.rows || [];
    return (meta.aggregates && meta.aggregates[group]) || [];
  };

  const filtersFor = (group) => {
    const base = [
      { key: 'start_date', label: 'Start date', type: 'date' },
      { key: 'end_date', label: 'End date', type: 'date' },
    ];
    if (group === 'none') {
      return [
        ...base,
        { key: 'org_id', label: 'Organisation', type: 'select', options: orgs.map(o => ({ value: o.id, label: `${o.name} (@${o.domain})` })) },
        { key: 'recruiter_email', label: 'Posted by (email contains)', type: 'text', placeholder: 'name@domain' },
        { key: 'status', label: 'Status', type: 'select', options: [
          { value: 'active', label: 'Active' }, { value: 'archived', label: 'Archived' }, { value: 'closed', label: 'Closed' },
        ] },
        { key: 'min_matches', label: 'Min matches', type: 'number' },
        { key: 'max_matches', label: 'Max matches', type: 'number' },
      ];
    }
    return base;
  };

  const defaultSelectedFor = (group) => {
    if (group === 'none') return ['job_id', 'title', 'status', 'organization_name', 'posted_by_email', 'created_at', 'total_matches'];
    return (columnsFor(group) || []).map(c => c.key);
  };

  return (
    <div data-testid="admin-reports">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-white tracking-tight flex items-center gap-2"><FileSpreadsheet className="w-5 h-5" /> Reports</h1>
        <p className="text-slate-500 dark:text-zinc-500 text-sm">Track job-posting activity across organisations, heads, and individual recruiters. Pick a grouping, filter by date, preview, then download CSV.</p>
      </div>

      {error && <div className="mb-4 px-4 py-2 rounded-lg bg-red-500/10 text-red-600 dark:text-red-400 text-sm flex items-center gap-2"><AlertCircle className="w-4 h-4" />{error}</div>}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {GROUP_OPTIONS.map(g => {
          const Icon = g.icon;
          return (
            <button key={g.key} onClick={() => setOpenGroup(g.key)} className="text-left bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 hover:border-slate-300 dark:hover:border-zinc-700 rounded-xl p-5 transition group" data-testid={`open-report-${g.key}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center"><Icon className="w-5 h-5 text-emerald-600 dark:text-emerald-400" /></div>
                <Download className="w-4 h-4 text-slate-500 dark:text-zinc-600 group-hover:text-slate-900 dark:group-hover:text-white transition" />
              </div>
              <h3 className="text-slate-900 dark:text-white font-medium text-sm">{g.label}</h3>
              <p className="text-slate-500 dark:text-zinc-500 text-xs mt-1">{g.desc}</p>
            </button>
          );
        })}
      </div>

      <ReportDialog
        open={!!openGroup}
        onClose={() => setOpenGroup(null)}
        title={openGroup ? (GROUP_OPTIONS.find(g => g.key === openGroup)?.label || 'Report') : 'Report'}
        columns={openGroup ? columnsFor(openGroup) : []}
        defaultSelected={openGroup ? defaultSelectedFor(openGroup) : []}
        defaultFilters={{ start_date: monthAgo, end_date: today }}
        filterSchema={openGroup ? filtersFor(openGroup) : []}
        fetchPreview={(f, cs) => reportsAPI.adminPreview({ ...f, group_by: openGroup }, cs)}
        downloadCsv={(f, cs) => reportsAPI.adminDownload({ ...f, group_by: openGroup }, cs)}
      />
    </div>
  );
}
